import { authenticatedFetch, guardedFetch } from "./utils";
import type { ApiResponse } from "../../types/api";
import API_CONSTANTS from "../constants/api";
import { type Campaign, type CampaignUser } from "../../types/campaign";
import type { WorkflowProcess } from "../../types/workflowProcess";

export type CampaignResponse = ApiResponse & {
  result: Campaign;
};

export type CampaignUserResponse = ApiResponse & {
  campaign_user: CampaignUser;
};

export type StartEmailVerificationResponse = ApiResponse & {
  email: string;
};

type AnonymousAuthStartResponse = ApiResponse & {
  process_key: string;
};

export type AnonymousAuthData = {
  client: string;
  fingerprint: string;
  workflow_key: string;
  fingerprint_checksum?: string;
  channel?: string;
  expiry?: string;
  item_key?: string;
  variation?: string;
  social_account?: string;
  skip_stock_management?: boolean;
  social_gifting?: boolean;
  count?: number;
  type?: string;
  collection?: string;
  delivery_service?: string;
};

export type GiftPreviewResponse = ApiResponse & {
  gift: {
    visible_name: string;
    description: string;
    image: string;
  };
};

export type WorkflowProcessResponse = ApiResponse & {
  process: WorkflowProcess;
};

export async function anonymousStart(data: AnonymousAuthData): Promise<AnonymousAuthStartResponse> {
  const url = `${API_CONSTANTS.frontendBaseUrl}/v1/campaign/anonymous/start`;
  const result = await guardedFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  });
  return (await result.json()) as AnonymousAuthStartResponse;
}

export async function getCampaign(campaignKey: string, timestamp: number): Promise<CampaignResponse> {
  const url = `${API_CONSTANTS.frontendBaseUrl}/v1/campaign?campaign_key=${campaignKey}&timestamp=${timestamp}`;
  const result = await guardedFetch(url, {
    method: "GET"
  });
  return (await result.json()) as CampaignResponse;
}

export async function checkCampaignValidity(campaignKey: string, timestamp: number): Promise<ApiResponse> {
  const url =
    `${API_CONSTANTS.frontendBaseUrl}/v1/campaign/is-valid` + `?campaign_key=${campaignKey}&timestamp=${timestamp}`;
  const result = await guardedFetch(url, {
    method: "GET"
  });
  return (await result.json()) as ApiResponse;
}

export async function getCampaignUser(): Promise<CampaignUserResponse> {
  const url = `${API_CONSTANTS.frontendBaseUrl}/v1/campaign-user`;
  const result = await authenticatedFetch(url, {
    method: "GET"
  });
  return (await result.json()) as CampaignUserResponse;
}

export async function continueWorkflow(processKey: string): Promise<ApiResponse> {
  const url = `${API_CONSTANTS.frontendBaseUrl}/v1/campaign/continue`;
  const result = await authenticatedFetch(url, {
    method: "POST",
    body: JSON.stringify({
      process_key: processKey
    })
  });
  return (await result.json()) as CampaignUserResponse;
}

export async function activateWorkflow(selectedAddressId: number): Promise<ApiResponse> {
  const url = `${API_CONSTANTS.frontendBaseUrl}/v1/user/activate-workflow`;
  const result = await authenticatedFetch(url, {
    method: "POST",
    body: JSON.stringify({
      address_id: selectedAddressId
    })
  });
  return (await result.json()) as CampaignUserResponse;
}

export async function startEmailVerification(campaignKey: string): Promise<StartEmailVerificationResponse> {
  const url = `${API_CONSTANTS.authApiBaseUrl}/start-email-verification`;
  const result = await authenticatedFetch(url, {
    method: "POST",
    body: JSON.stringify({
      campaign_key: campaignKey
    })
  });
  return (await result.json()) as StartEmailVerificationResponse;
}

export async function getWorkflowInfo(workflowProcessKey: string): Promise<WorkflowProcessResponse> {
  const url = `${API_CONSTANTS.frontendBaseUrl}/v1/workflows/info/${workflowProcessKey}`;
  const result = await guardedFetch(url, { method: "GET" });
  return (await result.json()) as WorkflowProcessResponse;
}

export async function resetCampaignUserPhase(): Promise<ApiResponse> {
  const url = `${API_CONSTANTS.frontendBaseUrl}/v1/campaign-user/reset-phase`;
  const result = await authenticatedFetch(url, { method: "POST" });
  return (await result.json()) as ApiResponse;
}

export async function getGiftPreview({
  itemKey,
  variation
}: {
  itemKey: string;
  variation: string;
}): Promise<GiftPreviewResponse> {
  const params = new URLSearchParams({ item_key: itemKey, variation });
  const url = `${API_CONSTANTS.serverlessFunctionsBaseUrl}/gifts/preview?${params.toString()}`;
  const result = await guardedFetch(url, { method: "GET" });
  return (await result.json()) as GiftPreviewResponse;
}
